import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useCurrentJob } from '../contexts/LayoutContext';
import { PageHeader, Banner, WideSection } from '../components/ui';
import { jobTabsFor } from '../nav/sections';
import { jobPath } from '../nav/paths';
import { NoAccess } from './JobsPages';
import JobBudgetView from '../components/JobBudgetView';
import JobPlanningLinesView from './JobPlanningLinesView';
import CreateInvoicesView from '../components/CreateInvoicesView';
import { InvoiceApprovalJob } from '../components/InvoiceApprovalView';
import InvoicesView from '../components/InvoicesView';

// One job: /jobs/:jobNo/:tab — Budget, Entries, Create Invoices, Invoice Approval
// and Receivables for that job (tabs in the app header's sub-nav).
export default function JobPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const params = useParams();
  const jobNo = String(params.jobNo || '').trim();
  const [job, setJob] = useState(null);
  const [error, setError] = useState('');
  const { setJob: setCurrentJob } = useCurrentJob();
  useEffect(() => {
    let cancelled = false;
    setJob(null);
    setError('');
    api.getJobHeader(jobNo)
      .then((r) => {
        if (cancelled) return;
        setJob(r.job || null);
        // Tells the app header whether to show the billing tabs.
        setCurrentJob({ jobNo, isOverhead: !!(r.job && Number(r.job.IsOverhead)) });
      })
      .catch((e) => { if (!cancelled) setError((e && e.message) || 'Failed to load the job'); });
    return () => { cancelled = true; setCurrentJob(null); };
  }, [jobNo, setCurrentJob]);

  // An overhead job is never invoiced: no Create Invoices / Invoice Approval /
  // Receivables tabs.
  const isOverhead = !!(job && Number(job.IsOverhead));
  const tabs = jobTabsFor(user, isOverhead);
  const tab = tabs.find((t) => t.key === params.tab);

  if (!tabs.length) return <NoAccess />;
  if (!tab) return <Navigate replace to={jobPath(jobNo, tabs[0].key)} />;

  const person = (name, no) => (name || no || '—');
  const subtitle = job && [
    isOverhead ? 'Overhead' : (job.CustomerName || job.CustomerNo),
    `PM: ${person(job.ProjectManagerName, job.ProjectManager)}`,
    `Director: ${person(job.DirectorName, job.Director)}`,
  ].filter(Boolean).join('  ·  ');

  return (
    <>
      <div style={{ padding: '4px 16px 0' }}>
        <PageHeader
          onBack={() => navigate(-1)} backTitle="Back"
          title={`${jobNo}${job && job.JobDescription ? ` — ${job.JobDescription}` : ''}`}
          subtitle={subtitle}
        />
        <Banner kind="err" onClose={() => setError('')}>{error}</Banner>
      </div>
      {tab.key === 'budget' && (
        <JobBudgetView jobNo={jobNo} />
      )}
      {tab.key === 'entries' && (
        <WideSection>
          <JobPlanningLinesView job={jobNo} task="" inPage />
        </WideSection>
      )}
      {tab.key === 'invoicing' && (
        <CreateInvoicesView key={jobNo} jobNo={jobNo} onOpenBudget={() => navigate(jobPath(jobNo, 'budget'))} />
      )}
      {tab.key === 'approval' && (
        <InvoiceApprovalJob jobNo={jobNo} />
      )}
      {tab.key === 'receivables' && (
        <InvoicesView deepLinkJob={jobNo} />
      )}
    </>
  );
}
